/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Tag,
  Modal,
  Alert,
  Dropdown,
  Select,
  message,
} from "antd";
import {
  EyeOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { refundsApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";
import ResponsiveTable from "../../_components/ResponsiveTable";

const { Option } = Select;

const STATUS_CONFIG = {
  pending:   { color: "orange" },
  approved:  { color: "blue" },
  rejected:  { color: "red" },
  completed: { color: "green" },
};

const StatusTag = ({ status }) => {
  const config = STATUS_CONFIG[status] || { color: "default" };
  return (
    <Tag color={config.color}>{status?.replace(/_/g, " ").toUpperCase()}</Tag>
  );
};

const ReasonCell = ({ reason }) => (
  <span
    title={reason}
    style={{
      display: "block",
      maxWidth: 180,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontSize: 13,
      color: reason ? "#374151" : "#9ca3af",
    }}
  >
    {reason || "—"}
  </span>
);

const RefundsList = () => {
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({ page: 1, limit: 10, status: "" });
  const [actionLoading, setActionLoading] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);

  const [approveModal, setApproveModal] = useState({ visible: false, id: null, notes: "" });
  const [rejectModal,  setRejectModal]  = useState({ visible: false, id: null, reason: "" });
  const [completeModal, setCompleteModal] = useState({ visible: false, id: null, reference: "" });

  useEffect(() => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const userData = JSON.parse(userStr);
        let permissions = [];
        if (userData.permissions && Array.isArray(userData.permissions)) {
          permissions = userData.permissions;
        } else if (userData.role?.permissions) {
          permissions = userData.role.permissions;
        }
        setUserPermissions(permissions.map((p) => (typeof p === "string" ? p : p.name)));
      }
    } catch (error) {
      console.error("Error loading user:", error);
    } finally {
      setIsCheckingPermission(false);
    }
  }, []);

  const canViewRefunds    = useMemo(() => hasPermission(userPermissions, "reservations.refund"), [userPermissions]);
  const canManageRefunds  = useMemo(() => hasPermission(userPermissions, "reservations.refund"), [userPermissions]);

  useEffect(() => {
    if (!isCheckingPermission && canViewRefunds) fetchRefunds();
  }, [filters, canViewRefunds, isCheckingPermission]);

  const fetchRefunds = async () => {
    try {
      setLoading(true);
      const data = await refundsApi.getAll({ ...filters });
      setRefunds(data.data || []);
      setTotalItems(
        data.totalPages
          ? data.totalPages * filters.limit
          : (data.data || []).length,
      );
    } catch (error) {
      console.error("Error fetching refunds:", error);
      message.error(error.response?.data?.message || "Failed to load refunds");
    } finally {
      setLoading(false);
    }
  };

  // ── Handlers ────────────────────────────────────────────

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await refundsApi.approve(approveModal.id, approveModal.notes || undefined);
      message.success("Refund approved");
      setApproveModal({ visible: false, id: null, notes: "" });
      fetchRefunds();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to approve refund");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.reason.trim()) {
      message.error("Rejection reason is required");
      return;
    }
    try {
      setActionLoading(true);
      await refundsApi.reject(rejectModal.id, rejectModal.reason);
      message.success("Refund rejected");
      setRejectModal({ visible: false, id: null, reason: "" });
      fetchRefunds();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to reject refund");
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      setActionLoading(true);
      await refundsApi.complete(completeModal.id, completeModal.reference || null);
      message.success("Refund marked as completed");
      setCompleteModal({ visible: false, id: null, reference: "" });
      fetchRefunds();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to complete refund");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Columns ─────────────────────────────────────────────

  const columns = [
    {
      title: "Ticket",
      key: "ticket",
      width: 140,
      render: (_, refund) => (
        <code style={{ background: "#f3f4f6", padding: "2px 6px", borderRadius: 4 }}>
          {refund.ticket?.ticketNumber || "—"}
        </code>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      render: (_, refund) => {
        const r = refund.ticket?.reservation;
        const user = r?.user;
        const name = user
          ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
          : r?.guestName || r?.guestEmail || "Guest";
        const email = user?.email || r?.guestEmail;
        const phone = r?.guestPhone || user?.phone;
        return (
          <div>
            <strong>{name}</strong>
            {email && <div style={{ fontSize: 12, color: "#6b7280" }}>{email}</div>}
            {phone && (
              <div style={{ fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
                <PhoneOutlined style={{ fontSize: 10 }} />
                {phone}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Trip",
      key: "trip",
      render: (_, refund) =>
        refund.ticket?.reservation?.tripDate?.trip?.name || "—",
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      width: 120,
      render: (_, refund) => `EGP ${Number(refund.amount || 0).toFixed(2)}`,
    },
    {
      title: "Reason",
      key: "reason",
      render: (_, refund) => (
        <div>
          <ReasonCell reason={refund.reason} />
          {refund.notes && (
            <div
              title={refund.notes}
              style={{ fontSize: 11, color: "#6b7280", marginTop: 2, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {refund.notes}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, refund) => <StatusTag status={refund.status} />,
    },
    {
      title: "Requested",
      key: "createdAt",
      width: 110,
      render: (_, refund) => new Date(refund.createdAt).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, refund) => {
        const items = [];

        // View details
        items.push({
          key: "view",
          label: "View Details",
          icon: <EyeOutlined />,
          onClick: () => {
            const r = refund.ticket?.reservation;
            const user = r?.user;
            const phone = r?.guestPhone || user?.phone;
            const paymentMethod = refund.payment?.method;
            Modal.info({
              title: "Refund Details",
              width: 600,
              content: (
                <div>
                  <p><strong>Ticket:</strong> {refund.ticket?.ticketNumber || "—"}</p>
                  <p><strong>Customer:</strong> {user?.email || r?.guestEmail || "Guest"}</p>
                  {phone && <p><strong>Phone:</strong> {phone}</p>}
                  <p><strong>Trip:</strong> {r?.tripDate?.trip?.name || "—"}</p>
                  <p><strong>Amount:</strong> EGP {Number(refund.amount).toFixed(2)}</p>
                  {paymentMethod && (
                    <p><strong>Payment Method:</strong> {paymentMethod.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                  )}
                  <p><strong>Reason:</strong> {refund.reason || "—"}</p>
                  {refund.notes && (
                    <p><strong>Customer Notes:</strong> {refund.notes}</p>
                  )}
                  <p><strong>Status:</strong> <StatusTag status={refund.status} /></p>
                  {refund.adminNotes && (
                    <p><strong>Admin Notes:</strong> {refund.adminNotes}</p>
                  )}
                  {refund.transactionReference && (
                    <p><strong>Bank / Payment Reference:</strong> <code>{refund.transactionReference}</code></p>
                  )}
                </div>
              ),
            });
          },
        });

        if (refund.status === "pending" && canManageRefunds) {
          items.push({ type: "divider" });
          items.push({
            key: "approve",
            label: "Approve",
            icon: <CheckCircleOutlined style={{ color: "#16a34a" }} />,
            onClick: () => setApproveModal({ visible: true, id: refund.id, notes: "" }),
          });
          items.push({
            key: "reject",
            label: "Reject",
            icon: <CloseCircleOutlined />,
            danger: true,
            onClick: () => setRejectModal({ visible: true, id: refund.id, reason: "" }),
          });
        }

        if (refund.status === "approved" && canManageRefunds) {
          items.push({ type: "divider" });
          items.push({
            key: "complete",
            label: "Mark Completed",
            icon: <DollarOutlined />,
            onClick: () => setCompleteModal({ visible: true, id: refund.id, reference: "" }),
          });
        }

        if (items.length === 1) {
          return (
            <Button type="text" icon={<EyeOutlined />} onClick={() => items[0].onClick()} />
          );
        }

        return (
          <Dropdown menu={{ items }} placement="bottomRight" trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  // ── Guards ──────────────────────────────────────────────

  if (isCheckingPermission) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Checking permissions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!canViewRefunds) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              type="error"
              message="Access Denied"
              description={
                <div>
                  <p>You don't have permission to view refund requests.</p>
                  <p className="mt-2"><strong>Required permission:</strong> reservations.refund</p>
                  <p className="mt-2"><strong>Your permissions:</strong> {userPermissions.length > 0 ? userPermissions.join(", ") : "None"}</p>
                </div>
              }
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0 premium-card">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Refund Management</h5>
                </div>
              </div>

              <ResponsiveTable
                columns={columns}
                dataSource={refunds}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: filters.page,
                  pageSize: filters.limit,
                  total: totalItems,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} refunds`,
                }}
                onChange={(pagination) =>
                  setFilters((prev) => ({
                    ...prev,
                    page: pagination.current,
                    limit: pagination.pageSize,
                  }))
                }
                scroll={{ x: true }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      <Modal
        title="Approve Refund"
        open={approveModal.visible}
        onOk={handleApprove}
        onCancel={() => setApproveModal({ visible: false, id: null, notes: "" })}
        confirmLoading={actionLoading}
        okText="Confirm Approval"
        okButtonProps={{ style: { background: "#16a34a", borderColor: "#16a34a" } }}
      >
        <Alert
          message="This refund will be approved."
          description="The customer will receive an approval notification email."
          type="success"
          showIcon
          className="mb-3"
        />
        <div style={{ marginTop: 16 }}>
          <label style={{ fontWeight: 600 }}>Admin Notes (optional)</label>
          <textarea
            rows={3}
            className="form-control"
            value={approveModal.notes}
            onChange={(e) => setApproveModal({ ...approveModal, notes: e.target.value })}
            placeholder="Optional notes for internal reference"
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject Refund"
        open={rejectModal.visible}
        onOk={handleReject}
        onCancel={() => setRejectModal({ visible: false, id: null, reason: "" })}
        confirmLoading={actionLoading}
        okText="Confirm Rejection"
        okType="danger"
      >
        <Alert
          message="This refund will be rejected."
          description="The customer will receive a rejection email with the reason you provide."
          type="error"
          showIcon
          className="mb-3"
        />
        <div style={{ marginTop: 16 }}>
          <label style={{ fontWeight: 600 }}>
            Rejection Reason <span style={{ color: "red" }}>*</span>
          </label>
          <textarea
            rows={3}
            className="form-control"
            value={rejectModal.reason}
            onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
            placeholder="Provide a clear reason for rejection (required)"
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>

      {/* Complete Modal */}
      <Modal
        title="Mark Refund as Completed"
        open={completeModal.visible}
        onOk={handleComplete}
        onCancel={() => setCompleteModal({ visible: false, id: null, reference: "" })}
        confirmLoading={actionLoading}
        okText="Mark Completed"
      >
        <Alert
          message="Confirm that the money has been returned to the customer."
          description="This marks the refund as done and sends the customer a confirmation email."
          type="info"
          showIcon
          className="mb-3"
        />
        <div style={{ marginTop: 16 }}>
          <label style={{ fontWeight: 600 }}>
            Bank / Payment Reference ID <span style={{ fontWeight: 400, color: "#6b7280" }}>(optional)</span>
          </label>
          <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 8px" }}>
            The reference number from the bank transfer or payment gateway (e.g. Vodafone Cash transfer ID, InstaPay reference). Stored for your records.
          </p>
          <input
            type="text"
            className="form-control"
            value={completeModal.reference}
            onChange={(e) => setCompleteModal({ ...completeModal, reference: e.target.value })}
            placeholder="e.g. TXN-20260428-00123"
          />
        </div>
      </Modal>
    </div>
  );
};

export default RefundsList;
